import React, { useEffect, useState } from 'react';
import { redirect, useOutletContext } from 'react-router';
import { useLocalizedNavigate } from '@app/app/i18n';

import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';

import {
  useGetCommonInitQuery,
  useLazyGetUserInitQuery,
  useLoginMutation,
} from '@app/store/api/user.api';
import { isLocalhost } from '@app/app/lib';
import { route_path } from '@app/utils/route_path';
import { saveTemplatesParams, buildGoogleOAuthUrl } from '@app/utils/templates-params';
import { useTranslation, useLanguages } from '@app/app/i18n';
import { LocalizedLink } from '@app/shared/ui/localized-link';
import { LanguageSwitcher } from '@app/widgets/header/ui/components/language-switcher';
import { WorldIcon } from '@app/components/ui/icons';
import { cvaIcon } from '@app/shared/ui/elements';

import { PlatformName, SignInUpMethod } from '@app/app/analytics/types';
import { CommonOutletContext, detectCustomerCategory } from '@app/app/layouts';
import { cn } from '@app/app/lib/cn';
import { useAppDispatch } from '@app/hooks/use-store';
import { loginSuccess } from '@app/store/slice/auth.slice';
import { COOKIE_KEY, cookieUtils } from '@app/app/cookie';
import { analytics } from '@app/app/analytics/analytics';
import { Spinner } from '@app/shared/ui/spinner';
import { setLanguageCookie } from '@app/app/i18n/utils/language-cookie';
import i18n from '@app/app/i18n/config';
import { usePageMeta } from '@app/hooks/use-page-meta';
import { isBoxedMode } from '@app/utils/user-status.utils';

interface LoginResponseWithWelcome {
  status: boolean;
  errors?:
    | {
        __all__?: string[];
        [key: string]: unknown;
      }
    | string
    | null;
  welcome_message?: string;
  welcome_button?: string;
}

type LoginFields = {
  username: string;
  password: string;
};

const LoginPage = () => {
  const { t } = useTranslation('auth');
  const meta = usePageMeta({ title: t('login.title') });
  const { currentLanguage } = useTranslation('header');
  const languages = useLanguages('header');
  const { context, commonContext } = useOutletContext<CommonOutletContext>();
  const { data: commonInit, isLoading: isCommonInitLoading } = useGetCommonInitQuery(null);
  const isBoxed = isBoxedMode(commonContext);
  console.log('commonContext', commonInit);

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  const [authType, setAuthType] = useState<SignInUpMethod>(
    commonContext?.social_auth_available ? 'google' : 'email',
  );
  const [showPassword, setShowPassword] = useState(false);
  const [getUserInit, { isLoading: isUserInitLoading }] = useLazyGetUserInitQuery();
  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation();
  const navigate = useLocalizedNavigate();
  const {
    register,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = useForm<LoginFields>({});

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (typeof context !== 'undefined') redirect(route_path.main);
  }, [context]);

  function submitHandler(data: LoginFields) {
    const formData = new FormData();
    Object.entries(data).forEach(([field, value]) => {
      formData.append(field, value);
    });

    loginMutation({ body: formData })
      .unwrap()
      .then((resultOfLogin: LoginResponseWithWelcome) => {
        if (resultOfLogin.status) {
          dispatch(loginSuccess());

          analytics.emitEvent('login', {
            [PlatformName.GTM]: {
              method: 'email',
            },
          });

          // Check if there's a welcome_message in the response
          if (
            resultOfLogin.welcome_message &&
            localStorage.getItem('hasSeenWelcomeMessage') !== 'true'
          ) {
            // Save welcome message to localStorage for common.outlet handling
            localStorage.setItem('pendingWelcomeMessage', resultOfLogin.welcome_message);
          }
          if (resultOfLogin.welcome_button) {
            localStorage.setItem('pendingWelcomeMessageButtonText', resultOfLogin.welcome_button);
          }

          //FIXME: should be moved to common.outlet level
          getUserInit(null)
            .unwrap()
            .then((userData) => {
              if (userData?.result) {
                // Устанавливаем куку языка из профиля (если есть)
                if (userData.result.lang) {
                  setLanguageCookie(userData.result.lang, true);
                  i18n.changeLanguage(userData.result.lang);
                }

                const customer_category = detectCustomerCategory(userData.result);
                if (!cookieUtils.get(COOKIE_KEY.customerCategory)) {
                  cookieUtils.set(COOKIE_KEY.customerCategory, customer_category);
                }
                const userId = userData.result.user_ctx.id;
                const isUserIdEven = userId !== undefined; // && userId % 2 === 0;
                const isWorkspaceDefault = isUserIdEven; // isBoxed ||

                const url = new URL(location.href);
                // Сохраняем параметры templates из URL перед редиректом
                saveTemplatesParams(url.searchParams);
                const nextParam = url.searchParams.get('next');
                if (!nextParam) {
                  // Если параметра next нет, перенаправляем на route_path.create
                  return navigate(
                    isWorkspaceDefault ? route_path.workspaceCreate : route_path.create,
                  );
                }

                const next = decodeURIComponent(nextParam);
                const forbiddenRoutes = ['logout', 'setup-style', 'upload-draft', 'login'];

                // Проверяем, содержит ли next запрещённые маршруты
                if (forbiddenRoutes.some((route) => next.toLowerCase().includes(route))) {
                  return navigate(
                    isWorkspaceDefault ? route_path.workspaceCreate : route_path.create,
                  );
                }

                // Внешний redirect — если next содержит полный URL, перенаправляем как есть
                if (next.startsWith('http')) {
                  location.href = next;
                  return;
                }

                // Локальный redirect — убираем параметр и переходим
                url.searchParams.delete('next');
                url.pathname = next;
                location.href = url.pathname;
              }
            });
        } else {
          if (
            resultOfLogin.errors !== null &&
            typeof resultOfLogin.errors === 'object' &&
            '__all__' in resultOfLogin.errors
          ) {
            throw new Error(resultOfLogin.errors?.__all__?.[0] ?? 'Unknown error');
          } else {
            throw new Error('Unknown error');
          }
        }
      })
      .catch((error) => {
        setError('password', { type: 'serverError', message: error.message });
      });
  }

  return (
    <div className="auth_wrap auth_wrap-new">
      <Helmet>
        <title>{meta.title}</title>
        {meta.description && <meta name="description" content={meta.description} />}
        <meta property="og:title" content={meta.ogTitle} />
        {meta.ogDescription && <meta property="og:description" content={meta.ogDescription} />}
        <meta property="og:url" content={meta.ogUrl} />
        <meta property="og:locale" content={meta.ogLocale} />
        <meta property="og:image" content={meta.ogImage} />
      </Helmet>

      {/* Language switcher */}
      <div className="absolute top-6 right-6 z-50">
        <LanguageSwitcher
          side="bottom"
          trigger={
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-general_background_MIII_500_dark/80 hover:bg-general_background_MIII_500_dark text-general_text_MII_default hover:text-general_MI_500_default transition-colors"
            >
              <WorldIcon className={cvaIcon()} />
              <span>{currentLang.flag}</span>
            </button>
          }
        />
      </div>

      <div className="auth_info_wrap">
        <div className="auth_info">
          <img className="auth_info_img" src="/static/i/w16.webp" alt="W" />
          <div className="auth_info_title">
            {t('login.welcome')}
            <br />
            {t('login.seeYou')}
          </div>
          <div className="auth_info_text">{t('login.headerDesc')}</div>
        </div>
      </div>

      <div className={'auth auth-login' + (authType === 'email' ? ' auth-email' : '')}>
        <div className="auth_block">
          <div className="auth_menu">
            <span className="auth_menu_item active">{t('login.login')}</span>
            <LocalizedLink className="auth_menu_item" to={'/register/' + (location.search || '')}>
              {t('login.signup')}
            </LocalizedLink>
          </div>

          {/* Ждем загрузки commonContext, чтобы не было мигания между режимами */}
          {!commonContext ? (
            isCommonInitLoading ? (
              <div className="flex justify-center items-center py-8">
                <Spinner size="large" />
              </div>
            ) : null
          ) : commonContext.keycloak_auth && !isLocalhost ? (
            <a className="auth_keycloak_btn" href={'/auth/authenticate/' + location.search}>
              {t('login.continueWithKeycloak')}
            </a>
          ) : (
            <>
              {authType === 'email' && (
                <form className="auth_form" onSubmit={handleSubmit(submitHandler)}>
                  <div className="auth_row auth_row-email">
                    <input
                      className="auth_input"
                      {...register('username', {
                        required: { value: true, message: t('login.errors.required') },
                      })}
                      type="email"
                      placeholder={t('login.email')}
                      required
                      autoFocus
                    />
                    {errors.username && (
                      <div className="auth_error auth_error-all">{errors.username?.message}</div>
                    )}
                  </div>

                  <div className="auth_row auth_row-pwd">
                    <input
                      className="auth_input"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', {
                        required: { value: true, message: t('login.errors.required') },
                      })}
                      placeholder={t('login.password')}
                      autoComplete="current-password"
                    />
                    <span
                      className="auth_show_pwd"
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    ></span>
                    {errors.password && (
                      <div className="auth_error auth_error-all">{errors.password?.message}</div>
                    )}
                  </div>

                  <button
                    className={cn(
                      'auth_submit gen_btn_main',
                      isLoginLoading && isUserInitLoading && 'opacity-80 animate-pulse',
                    )}
                    disabled={isLoginLoading || isUserInitLoading || isSubmitting}
                    type="submit"
                  >
                    {t('login.login')}
                  </button>
                </form>
              )}

              {commonContext?.social_auth_available && (
                <a className="auth_google_btn" href={buildGoogleOAuthUrl(location.search)}>
                  {t('login.continueWithGoogle')}
                </a>
              )}

              {authType === 'google' && commonContext?.social_auth_available && (
                <span
                  className="auth_by_email about_plain_link"
                  onClick={() => {
                    setAuthType('email');
                  }}
                >
                  {t('login.loginByEmail')}
                </span>
              )}

              {authType === 'email' && (
                <LocalizedLink className="auth_forgot_link about_plain_link" to="/password-reset/">
                  {t('login.iForgotMyPassword')}
                </LocalizedLink>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
